"use client";

import { InstitutionDetails } from "./InstitutionDetails";
import { ButtonData, ContactButtons } from "./ContactButtons";
import { RouterOutputs, api } from "@/lib/trpc/client";
import { Card } from "@/components/ui/card";
import { Credits } from "@/components/Credits";
import { AtSign, Landmark, Phone, Users } from "lucide-react";
import React from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import OrgChartSection from "./OrgChartSection";
import { translations } from "@/messages";
import { useLocale, useTranslations } from "use-intl";
import { replaceParams } from "@/utils/replaceParams";
import { getEmailRequests } from "@/templates/emailRequests";
import { Input } from "@/components/ui/input";
import HappyManCalling from "./assets/happy-call.png";
import { Icon } from "@iconify/react";
function formatPhoneNumber(phone: string): string {
  const cleanedPhone = phone.replace(/[\(\)\-\s]/g, "");
  if (cleanedPhone.startsWith("+")) {
    return cleanedPhone.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d{3,})/, "$1 $2 $3 $4");
  } else {
    return cleanedPhone.replace(/(\d{4})(\d{3})(\d{3,})/, "$1 $2 $3");
  }
}
interface InstitutionSidebarCardProps {
  institution: RouterOutputs["institutionsRouter"]["getInstitutionBySlug"];
  populationData?: RouterOutputs["adminPopulationsRouter"]["getRegionPopulationData"]["data"] | null;
}
export const InstitutionSidebarCard: React.FC<InstitutionSidebarCardProps> = props => {
  const [isOrgChartOpen, setIsOrgChartOpen] = React.useState(false);
  const [isCallDialogOpen, setIsCallDialogOpen] = React.useState(false);
  const locale = useLocale() as keyof typeof translations;
  const logoUrl = props.institution.logoUrl;
  const lastPopulationRecord = props.populationData?.sort((a, b) => a.year - b.year)[0];
  const t = useTranslations();
  const {
    data: departmentsData
  } = api.departmentRouter.getDepartmentsForInstitution.useQuery({
    institutionId: props.institution.id
  });
  const title = props.institution.name;
  const description = props.institution.details;
  const buttonList = [props.institution.websiteUrl ? {
    label: translations[locale]["institution"]["officialWebsite"],
    href: props.institution.websiteUrl,
    icon: <Landmark size={20} />
  } : undefined, props.institution.phone ? {
    label: translations[locale]["institution"]["callTownHall"],
    onClick: () => setIsCallDialogOpen(true),
    icon: <Phone size={20} />
  } : undefined, props.institution.email ? {
    label: translations[locale]["institution"]["sendEmail"],
    href: `mailto:${props.institution.email}`,
    icon: <AtSign size={20} />
  } : undefined, departmentsData?.length ? {
    label: translations[locale]["institution"]["townHallOrganigram"],
    onClick: () => setIsOrgChartOpen(true),
    icon: <Users size={20} />
  } : undefined].filter(Boolean) as ButtonData[];
  const emailRequests = getEmailRequests(props.institution.name);
  const randomEmailRequeqst = emailRequests[Math.floor(Math.random() * emailRequests.length)];
  const formattedPhoneNumber = formatPhoneNumber(props.institution.phone || "");
  return <div className="static z-10 justify-between gap-8 mobile:fixed mobile:inset-y-0 mobile:right-0 mobile:flex mobile:h-screen mobile:flex-col" data-sentry-component="InstitutionSidebarCard" data-sentry-source-file="InstitutionSidebar.tsx">
      <Card className="relative flex w-full flex-col justify-between overflow-y-auto bg-background p-4 mobile:h-screen mobile:w-80 mobile:rounded-none mobile:p-7 mobile:shadow-lg" data-sentry-element="Card" data-sentry-source-file="InstitutionSidebar.tsx">
        <div>
          <div className="relative">
            <InstitutionDetails institutionLogo={logoUrl} title={title} description={description || ""} locale={locale} population={lastPopulationRecord} data-sentry-element="InstitutionDetails" data-sentry-source-file="InstitutionSidebar.tsx" />
            {!description && <p className="text-sm">
                {replaceParams(translations[locale]["institution"]["welcomeMessage"], {
              institution: props.institution.name
            })}
              </p>}
          </div>
          <hr className="my-4 border-t border-secondary" />
          <div className="flex flex-col space-y-3">
            <ContactButtons buttons={buttonList} data-sentry-element="ContactButtons" data-sentry-source-file="InstitutionSidebar.tsx" />
          </div>
        </div>
        <div className="monile:w-80 props.institution.name mt-12 hidden flex-col items-center gap-4 text-center mobile:flex">
          {!props.institution.servicePackages?.length && props.institution.locale === "ro" && randomEmailRequeqst && <div className="flex w-full flex-col gap-4 rounded-3xl border border-orange-300/50 bg-orange-100/40 p-4 text-left text-sm text-orange-700">
                <div>
                  <b>{props.institution.name}</b> nu beneficiază de guvernare digitală oferită de
                  <b>{t("src_app_uat_institutionSlug_components_InstitutionSidebar_tsx_0")}</b>.
                  Apasă pe butonul de mai jos pentru a solicita acestei instituții să se
                  digitalizeze folosind
                  <b>{t("src_app_uat_institutionSlug_components_InstitutionSidebar_tsx_1")}</b>.
                </div>
                <button className="w-full rounded-xl bg-orange-700 px-1 py-2 text-center text-base font-medium text-white transition-all hover:bg-orange-800 focus:outline-none focus:ring-4 focus:ring-orange-300 dark:bg-orange-600 dark:hover:bg-orange-700 dark:focus:ring-orange-800" onClick={() => {
            window.location.href = `mailto:${props.institution.email}?subject=${randomEmailRequeqst.subject}&body=${encodeURIComponent(randomEmailRequeqst.body)}`;
          }}>
                  {t("src_app_uat_institutionSlug_components_InstitutionSidebar_tsx_2")}
                </button>
              </div>}
          <Credits data-sentry-element="Credits" data-sentry-source-file="InstitutionSidebar.tsx" />
        </div>
      </Card>

      <Dialog open={isCallDialogOpen} onOpenChange={setIsCallDialogOpen} data-sentry-element="Dialog" data-sentry-source-file="InstitutionSidebar.tsx">
        <DialogContent className="min-h-[24rem] max-w-sm overflow-hidden" data-sentry-element="DialogContent" data-sentry-source-file="InstitutionSidebar.tsx">
          <div style={{
          backgroundImage: `url(${HappyManCalling.src})`
        }} className="absolute left-0 top-0 h-full w-full bg-cover bg-center opacity-50" />
          <DialogHeader className="z-10 text-2xl font-semibold" data-sentry-element="DialogHeader" data-sentry-source-file="InstitutionSidebar.tsx">
            {t("institution.callTownHall")}
          </DialogHeader>
          <div className="align-end z-10 flex w-full flex-1 gap-4" style={{
          alignItems: "flex-end"
        }}>
            <div className="relative flex-1 drop-shadow-2xl">
              <Input type="text" value={formattedPhoneNumber} readOnly className="h-12 w-full cursor-text rounded-full" onClick={e => (e.target as HTMLInputElement).select()} data-sentry-element="Input" data-sentry-source-file="InstitutionSidebar.tsx" />
              <button className="absolute right-0 top-0 flex h-12 w-12 items-center justify-center rounded-full bg-blue-700 text-center text-base font-medium text-white transition-all hover:bg-blue-800" onClick={() => {
              window.location.href = `tel:${formattedPhoneNumber.replace(/\s/g, "")}`;
            }}>
                <Icon icon="line-md:phone-call-loop" className="h-5 w-5" data-sentry-element="Icon" data-sentry-source-file="InstitutionSidebar.tsx" />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isOrgChartOpen} onOpenChange={setIsOrgChartOpen} data-sentry-element="Dialog" data-sentry-source-file="InstitutionSidebar.tsx">
        <DialogContent className="h-screen max-h-[90vh] max-w-[90vw]" data-sentry-element="DialogContent" data-sentry-source-file="InstitutionSidebar.tsx">
          <OrgChartSection institutionSlug={props.institution.slug} data-sentry-element="OrgChartSection" data-sentry-source-file="InstitutionSidebar.tsx" />
        </DialogContent>
      </Dialog>
    </div>;
};